import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as Tooltip from '@radix-ui/react-tooltip';

import { selectAuthUser } from '../../../../redux/auth/auth.selectors';
import {
    selectCurrentWorkspace,
    selectWorkspacePlan,
    selectWorkspaceRestrictions,
    selectWorkspaceTeams,
    selectWorkspaceMembers,
} from '../../../../redux/workspace/workspace.selectors';
import {
    workspaceFetchTeamsRequest,
    workspaceFetchMembersRequest,
    workspaceUpdateRequest,
} from '../../../../redux/workspace/workspace.actions';
import { toggleModalWorkspaceDelete } from '../../../../redux/main/main.actions';
import { getAvatarUrl } from '../../../../utils/get-avatar-url';
import { getPlan } from '../../../../redux/workspace/workspace.plans';

const SettingsWorkspace = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const currentUser = useSelector(selectAuthUser);
    const workspace = useSelector(selectCurrentWorkspace);
    const plan = useSelector(selectWorkspacePlan) || 'free';
    const restrictions = useSelector(selectWorkspaceRestrictions) || getPlan(plan);
    const teams = useSelector(selectWorkspaceTeams) || [];
    const members = useSelector(selectWorkspaceMembers) || [];

    const [title, setTitle] = useState(workspace?.title || '');
    const [workspaceKey, setWorkspaceKey] = useState(workspace?.key || '');

    useEffect(() => {
        if (workspace?.id) {
            // dispatch(workspaceFetchTeamsRequest(workspace.id));
            dispatch(workspaceFetchMembersRequest(workspace.id));
        }
    }, [dispatch, workspace?.id]);

    useEffect(() => {
        setTitle(workspace?.title || '');
        setWorkspaceKey(workspace?.key || '');
    }, [workspace?.id, workspace?.title, workspace?.key]);

    const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
    const memberCount = members.length;
    const teamCount = teams.length;
    const isOwner = workspace?.my_role === 'owner' || workspace?.role === 'owner' || workspace?.owner_id === currentUser?.id;
    // A workspace on a paid plan must cancel its subscription before it can be
    // deleted (mirrors the backend `workspace_has_active_billing` guard).
    const isPaid = plan !== 'free';

    // Hero stats use the live counts; kept memoized for clarity, though
    // `memberCount` and `teamCount` are already cheap to derive.
    const stats = useMemo(() => ({ memberCount, teamCount }), [memberCount, teamCount]);

    // Save-on-blur: commit a single field when it loses focus, but only if its
    // trimmed value actually changed. Required fields revert to the persisted
    // value when blurred empty. On failure the saga notifies and we revert.
    const commitField = (field, value, setter, { required = false } = {}) => {
        if (!workspace?.id || !isOwner) return;
        const trimmed = value.trim();
        const persisted = workspace?.[field] || '';
        if (trimmed === persisted) return;
        if (required && !trimmed) {
            setter(persisted);
            return;
        }
        dispatch(workspaceUpdateRequest({
            id: workspace.id,
            fields: { [field]: trimmed },
            onError: () => setter(persisted),
        }));
    };

    return (
        <div className='settings-wrap'>
            <div className='settings-header'>{t('settings_page.workspace.header')}</div>

            {/* ── Hero card ─────────────────────────────────────── */}
            <div className='settings-hero hero-workspace'>
                <div className='settings-hero-avatar-wrap avatar-workspace'>
                    {/* {currentUser?.image
                        ? <img className='settings-hero-avatar-img' src={getAvatarUrl(currentUser.image)} referrerPolicy="no-referrer" alt="" />
                        : <div className='settings-hero-avatar-placeholder'>{(workspace?.title || 'M')[0].toUpperCase()}</div>
                    } */}
                    <div className='settings-hero-avatar-placeholder'>{(workspace?.title || 'M')[0].toUpperCase()}</div>
                </div>

                <div className='settings-hero-info'>
                    <div className='settings-hero-name'>{workspace?.title || '—'}</div>
                    <div className='settings-hero-email'>{planLabel}</div>
                </div>

                <div className='settings-hero-stats'>
                    <div className='settings-hero-stat'>
                        <span className='settings-hero-stat-value'>{stats.memberCount}</span>
                        <span className='settings-hero-stat-label'>{stats.memberCount === 1 ? t('settings_page.workspace.stat_member_singular') : t('settings_page.workspace.stat_member_plural')}</span>
                    </div>
                    <div className='settings-hero-stat'>
                        <span className='settings-hero-stat-value'>{stats.teamCount}</span>
                        <span className='settings-hero-stat-label'>{stats.teamCount === 1 ? t('settings_page.workspace.stat_team_singular') : t('settings_page.workspace.stat_team_plural')}</span>
                    </div>
                </div>
            </div>

            {/* ── General ──────────────────────────────────────── */}
            <div className='settings-subheader'>{t('settings_page.workspace.general')}</div>
            <div className='settings-section settings-section-list'>
                <div className='settings-fields'>
                    <div className='settings-field-row'>
                        <span className='settings-field-label'>{t('settings_page.workspace.name_label')}</span>
                        <input
                            className='settings-field-input'
                            type='text'
                            value={title}
                            maxLength={80}
                            placeholder={t('settings_page.workspace.name_placeholder')}
                            readOnly={!isOwner}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            onChange={(e) => setTitle(e.target.value)}
                            onBlur={() => commitField('title', title, setTitle, { required: true })}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                    </div>

                    {/* <div className="settings-field-line"></div>

                    <div className='settings-field-row'>
                        <span className='settings-field-label'>Workspace Key</span>
                        <input
                            className='settings-field-input'
                            type='text'
                            value={workspaceKey}
                            maxLength={16}
                            placeholder='KEY'
                            readOnly={!isOwner}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            onChange={(e) => setWorkspaceKey(e.target.value)}
                            onBlur={() => commitField('key', workspaceKey, setWorkspaceKey)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                    </div> */}
                </div>

            </div>

            {/* ── Members ──────────────────────────────────────── */}
            <div className='settings-subheader'>{t('settings_page.workspace.members_section')}</div>
            <div className='settings-section'>
                <div className='settings-row settings-row-small'>
                    <div className='settings-row-left'>
                        <span className='settings-row-sub'>
                            {memberCount === 0
                                ? t('settings_page.workspace.no_members')
                                : `${memberCount} ${memberCount === 1 ? t('settings_page.workspace.count_member_singular') : t('settings_page.workspace.count_member_plural')}`}
                        </span>
                    </div>
                    {/* {restrictions.canInviteMembers && (
                        <button className='settings-row-btn' onClick={() => dispatch(toggleInvitePeople())}>
                            Manage members
                        </button>
                    )} */}
                    <button className='settings-row-btn' onClick={() => history.push('/settings/members')}>
                        {t('settings_page.workspace.view_members_btn')}
                    </button>
                </div>
            </div>

            {/* ── Danger zone ──────────────────────────────────── */}
            <div className='settings-subheader'>{t('settings_page.workspace.danger_zone')}</div>
            <div className='settings-section'>
                <div className='settings-row'>
                    <div className='settings-row-left'>
                        <span className='settings-row-label'>{t('settings_page.workspace.delete_workspace')}</span>
                        <span className='settings-row-sub'>{t('settings_page.workspace.delete_workspace_sub')}</span>
                    </div>
                    {!isOwner ? (
                        <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <button className='settings-row-btn btn-delete' disabled>
                                    {t('settings_page.workspace.delete_workspace')}
                                </button>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls"
                                    side="bottom"
                                    align="center"
                                    sideOffset={8}
                                >
                                    <span>{t('settings_page.workspace.delete_owner_only_tooltip')}</span>
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    ) : isPaid ? (
                        <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <button className='settings-row-btn btn-delete' disabled>
                                    {t('settings_page.workspace.delete_workspace')}
                                </button>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls"
                                    side="bottom"
                                    align="center"
                                    sideOffset={8}
                                >
                                    <span>{t('settings_page.workspace.delete_cancel_sub_tooltip')}</span>
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    ) : (
                        <button
                            className='settings-row-btn btn-delete'
                            onClick={() => dispatch(toggleModalWorkspaceDelete(workspace))}
                            disabled={!workspace?.id}
                        >
                            {t('settings_page.workspace.delete_workspace')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SettingsWorkspace;
